import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Timer,
  Users,
  TrendingUp,
  BarChart3,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export default function AttendanceStats({ stats, loading, userRole }) {
  const formatDuration = (hours) => {
    if (!hours) return "0h 0m";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Check if this is employee-specific data
  const isEmployeeData = stats.userRole === "employe";

  // Calculate rates differently for employees vs admin
  const attendanceRate =
    !isEmployeeData && stats.totalEmployees > 0
      ? ((stats.presentToday / stats.totalEmployees) * 100).toFixed(1)
      : 0;

  const absenteeRate =
    !isEmployeeData && stats.totalEmployees > 0
      ? ((stats.absentToday / stats.totalEmployees) * 100).toFixed(1)
      : 0;

  const lateRate =
    !isEmployeeData && stats.totalEmployees > 0
      ? ((stats.lateToday / stats.totalEmployees) * 100).toFixed(1)
      : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                  </div>
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isEmployeeData ? (
          // Employee-specific stats
          <>
            <Card className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Mes présences
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {stats.totalPresences || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Total enregistré
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Mes absences
                    </p>
                    <p className="text-2xl font-bold text-red-600">
                      {stats.totalAbsences || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Absences validées
                    </p>
                  </div>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-orange-500 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Retard aujourd'hui
                    </p>
                    <p className="text-2xl font-bold text-orange-600">
                      {stats.isLateToday ? "Oui" : "Non"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Statut du jour
                    </p>
                  </div>
                  <div
                    className={`p-2 rounded-lg ${
                      stats.isLateToday ? "bg-orange-100" : "bg-green-100"
                    }`}
                  >
                    <AlertCircle
                      className={`h-6 w-6 ${
                        stats.isLateToday ? "text-orange-600" : "text-green-600"
                      }`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Temps moyen/jour
                    </p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatDuration(Number(stats.avgWorkingHours || 0))}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      H. sup.:{" "}
                      {formatDuration(Number(stats.overtimeThisMonth || 0))}
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Timer className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          // Admin/HR stats (original)
          <>
            <Card className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Présents aujourd'hui
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {stats.presentToday}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      sur {stats.totalEmployees} employés
                    </p>
                    <Progress
                      value={parseFloat(attendanceRate)}
                      className="mt-2 h-2"
                    />
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Absents aujourd'hui
                    </p>
                    <p className="text-2xl font-bold text-red-600">
                      {stats.absentToday}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {absenteeRate}% du personnel
                    </p>
                    <Progress
                      value={parseFloat(absenteeRate)}
                      className="mt-2 h-2"
                    />
                  </div>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-orange-500 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Retards aujourd'hui
                    </p>
                    <p className="text-2xl font-bold text-orange-600">
                      {stats.lateToday}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {lateRate}% des arrivées
                    </p>
                    <Progress
                      value={parseFloat(lateRate)}
                      className="mt-2 h-2"
                    />
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertCircle className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Temps moyen/jour
                    </p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatDuration(Number(stats.avgWorkingHours || 0))}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      H. sup.:{" "}
                      {formatDuration(Number(stats.overtimeThisMonth || 0))}
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Timer className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Detailed Stats Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {isEmployeeData ? "Mes statistiques" : "Statistiques détaillées"}
            </span>
            <Badge variant="outline" className="text-xs">
              Mis à jour:{" "}
              {new Date().toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isEmployeeData ? (
            // Employee detailed stats
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Total Presences */}
              <div className="text-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">
                  {stats.totalPresences || 0}
                </p>
                <p className="text-sm text-green-600">Présences</p>
                <p className="text-xs text-muted-foreground mt-1">Total</p>
              </div>

              {/* Total Absences */}
              <div className="text-center p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-700">
                  {stats.totalAbsences || 0}
                </p>
                <p className="text-sm text-red-600">Absences</p>
                <p className="text-xs text-muted-foreground mt-1">Validées</p>
              </div>

              {/* Late Status */}
              <div
                className={`text-center p-4 rounded-lg hover:opacity-90 transition-colors ${
                  stats.isLateToday
                    ? "bg-orange-50 hover:bg-orange-100"
                    : "bg-green-50 hover:bg-green-100"
                }`}
              >
                <AlertCircle
                  className={`h-8 w-8 mx-auto mb-2 ${
                    stats.isLateToday ? "text-orange-600" : "text-green-600"
                  }`}
                />
                <p
                  className={`text-2xl font-bold ${
                    stats.isLateToday ? "text-orange-700" : "text-green-700"
                  }`}
                >
                  {stats.isLateToday ? "Retard" : "À l'heure"}
                </p>
                <p
                  className={`text-sm ${
                    stats.isLateToday ? "text-orange-600" : "text-green-600"
                  }`}
                >
                  Aujourd'hui
                </p>
                <p className="text-xs text-muted-foreground mt-1">Statut</p>
              </div>

              {/* Average Hours */}
              <div className="text-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <Timer className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-700">
                  {Number(stats.avgWorkingHours || 0).toFixed(1)}h
                </p>
                <p className="text-sm text-blue-600">Moy. quotidienne</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatDuration(Number(stats.avgWorkingHours || 0))}
                </p>
              </div>
            </div>
          ) : (
            // Admin detailed stats (original)
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Present */}
              <div className="text-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">
                  {stats.presentToday}
                </p>
                <p className="text-sm text-green-600">Présents</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {attendanceRate}%
                </p>
              </div>

              {/* Absent */}
              <div className="text-center p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-700">
                  {stats.absentToday}
                </p>
                <p className="text-sm text-red-600">Absents</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {absenteeRate}%
                </p>
              </div>

              {/* Late */}
              <div className="text-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                <AlertCircle className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-orange-700">
                  {stats.lateToday}
                </p>
                <p className="text-sm text-orange-600">En retard</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {lateRate}%
                </p>
              </div>

              {/* Average Hours */}
              <div className="text-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <Timer className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-700">
                  {Number(stats.avgWorkingHours || 0).toFixed(1)}h
                </p>
                <p className="text-sm text-blue-600">Moy. quotidienne</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatDuration(Number(stats.avgWorkingHours || 0))}
                </p>
              </div>
            </div>
          )}

          {/* Additional Stats */}
          <div className="mt-6 pt-6 border-t space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {!isEmployeeData && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Total employés:</span>
                  <span className="font-medium">{stats.totalEmployees}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">
                  H. supplémentaires:
                </span>
                <span className="font-medium">
                  {Number(stats.overtimeThisMonth || 0).toFixed(1)}h
                </span>
              </div>
              {!isEmployeeData && (
                <>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">
                      Taux de présence:
                    </span>
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-200"
                    >
                      {attendanceRate}%
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">
                      Taux d'absence:
                    </span>
                    <Badge
                      variant="outline"
                      className="bg-red-50 text-red-700 border-red-200"
                    >
                      {absenteeRate}%
                    </Badge>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Department Breakdown if available */}
          {stats.departments && stats.departments.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Présence par département
              </h4>
              <div className="space-y-3">
                {stats.departments.map((dept, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="min-w-[100px]">
                        <p className="font-medium text-sm">{dept.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {dept.present}/{dept.total}
                        </p>
                      </div>
                      <div className="flex-1 max-w-[150px]">
                        <Progress
                          value={(dept.present / dept.total) * 100}
                          className="h-2"
                        />
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {((dept.present / dept.total) * 100).toFixed(1)}%
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Role-specific additional info */}
          {(userRole === "admin" ||
            userRole === "rh" ||
            userRole === "manager") && (
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Rôle: {userRole}</span>
                <span>
                  Dernière mise à jour: {new Date().toLocaleString("fr-FR")}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
